// src/pages/Developer.js
import { useContext, useState, useEffect } from 'react';
import { UserContext } from '../AuthWrapper';
import { FIELD_SCHEMA } from '../services/FirestoreProducts';
import { proxyFetch } from '../services/Proxy';

const PLUGIN_CODE_REGEX = /^(?=.*[A-Z])[A-Za-z0-9]{4}$/;

const FIELD_LABELS = {
  productType: 'Product Type',
  name: 'Name',
  pluginCode: 'Plugin Code',
  productCode: 'Shopify Product Code',
  internalProductCode: 'Internal Product Code',
  needsActivationCode: 'Needs Activation Code?',
  activationCodeSource: 'Activation Code Source',
  internalDownload: 'Internal Download?',
  bucket: 'Bucket',
  fileToDownload: 'File to Download',
  publicEncryptionKey: 'Public Encryption Key',
  privateEncryptionKey: 'Private Encryption Key',
  generateKeys: 'Generate Keys?',
  bundleItems: 'Bundle Items'
};

const FIELD_TOOLTIPS = {
  productType: 'Category of the product (instrument, effect, pack, etc.).',
  name: 'Display name shown to customers.',
  pluginCode: 'Exactly 4 letters/digits with at least one uppercase character. Must be unique.',
  productCode: 'Shopify product identifier.',
  internalProductCode: 'Auto-filled as pluginCode + productCode; read-only.',
  needsActivationCode: 'Tick if customers must enter an activation code.',
  activationCodeSource:
    '“internal” ⇒ you generate codes; “external” ⇒ third‑party service supplies codes.',
  generateKeys:
    'If ticked, the backend will generate key‑pairs automatically; you may leave key fields empty.',
  internalDownload: 'Tick if the download is in your own GCS bucket.',
  bucket:
    'Auto‑filled to match internalProductCode (lowercased) when internalDownload is enabled.',
  fileToDownload: 'Path to the ZIP / installer inside the bucket.',
  publicEncryptionKey: 'Public key for internal activation flow.',
  privateEncryptionKey: 'Private key paired with the public key.',
  bundleItems: 'Comma-separated list of productCodes that form the bundle.'
};

const ICON = 'ⓘ';

export default function Developer() {
  const { roles, user } = useContext(UserContext);

  const [form, setForm] = useState({
    productType: '',
    name: '',
    pluginCode: '',
    productCode: '',
    internalProductCode: '',
    needsActivationCode: false,
    activationCodeSource: '',
    generateKeys: false,
    internalDownload: false,
    bucket: '',
    fileToDownload: '',
    publicEncryptionKey: '',
    privateEncryptionKey: '',
    bundleItems: ''
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const { pluginCode, productCode, internalDownload } = form;

  useEffect(() => {
    const ipc = pluginCode && productCode ? `${pluginCode}${productCode}` : '';
    const bucketName = ipc.toLowerCase();

    setForm(prev => ({
      ...prev,
      internalProductCode: ipc,
      bucket: internalDownload ? bucketName : ''
    }));
  }, [pluginCode, productCode, internalDownload]);

  const resetForm = () =>
    setForm({
      productType: '',
      name: '',
      pluginCode: '',
      productCode: '',
      internalProductCode: '',
      needsActivationCode: false,
      activationCodeSource: '',
      generateKeys: false,
      internalDownload: false,
      bucket: '',
      fileToDownload: '',
      publicEncryptionKey: '',
      privateEncryptionKey: '',
      bundleItems: ''
    });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({
      ...p,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setStatus('');
    try {
      if (!PLUGIN_CODE_REGEX.test(form.pluginCode || '')) {
        throw new Error(
          'pluginCode must be 4 ASCII letters/digits with at least one uppercase.'
        );
      }

      if (
        form.internalProductCode !==
        `${form.pluginCode}${form.productCode}`
      ) {
        throw new Error(
          'Internal Product Code must equal pluginCode + productCode.'
        );
      }

      if (form.internalDownload) {
        if (
          form.bucket.toLowerCase() !==
          form.internalProductCode.toLowerCase()
        ) {
          throw new Error(
            'Bucket must match Internal Product Code (case-insensitive).'
          );
        }
        if (!form.fileToDownload) {
          throw new Error(
            'fileToDownload is required when internalDownload is enabled.'
          );
        }
      }

      if (
        form.needsActivationCode &&
        form.activationCodeSource === 'internal' &&
        !form.generateKeys
      ) {
        if (!form.publicEncryptionKey || !form.privateEncryptionKey) {
          throw new Error(
            'Public and Private keys are required unless “Generate Keys” is ticked.'
          );
        }
      }

      const token = await user.getIdToken();
      await proxyFetch(
        'proxy/product-utils/add',
        token,
        'POST',
        '',
        {},
        form
      );

      setStatus('✅ Product added successfully.');
      resetForm();
    } catch (err) {
      console.error(err);
      setStatus(err.message || 'Failed to add product.');
    } finally {
      setLoading(false);
    }
  };

  const schema = FIELD_SCHEMA[form.productType];
  const fieldsToRender = schema
    ? [
        ...schema.required,
        ...(schema.optional || [])
      ]
        .filter(f => f !== 'productType')
        .filter(f => {
          if (f === 'activationCodeSource')
            return form.needsActivationCode;
          if (f === 'generateKeys')
            return (
              form.needsActivationCode &&
              form.activationCodeSource === 'internal'
            );
          if (f === 'bucket') return form.internalDownload;
          if (
            ['publicEncryptionKey', 'privateEncryptionKey'].includes(f)
          ) {
            return (
              form.needsActivationCode &&
              form.activationCodeSource === 'internal' &&
              !form.generateKeys
            );
          }
          return true;
        })
    : [];

  if (
    form.internalDownload &&
    !fieldsToRender.includes('fileToDownload')
  ) fieldsToRender.push('fileToDownload');

  const tip = f => (
    <span
      style={{ cursor: 'help', fontSize: '0.9em' }}
      title={FIELD_TOOLTIPS[f]}
    >
      {ICON}
    </span>
  );

  const row = (f, control) => (
    <div
      key={f}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <label htmlFor={f} style={{ minWidth: 200 }}>
        {FIELD_LABELS[f]}
      </label>
      {control}
      {tip(f)}
    </div>
  );

  const renderField = f => {
    const v = form[f];
    if (f === 'activationCodeSource')
      return row(
        f,
        <select
          id={f}
          name={f}
          value={v}
          onChange={handleChange}
          style={{ flex: '1 1 auto' }}
        >
          <option value="">-- select --</option>
          <option value="internal">internal</option>
          <option value="external">external</option>
        </select>
      );
    if (
      ['needsActivationCode', 'internalDownload', 'generateKeys'].includes(f)
    )
      return (
        <div
          key={f}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <input
            id={f}
            type="checkbox"
            name={f}
            checked={!!v}
            onChange={handleChange}
          />
          <label htmlFor={f}>{FIELD_LABELS[f]}</label>
          {tip(f)}
        </div>
      );
    if (['internalProductCode', 'bucket'].includes(f))
      return row(
        f,
        <input
          id={f}
          type="text"
          name={f}
          value={v}
          readOnly
          style={{
            flex: '1 1 auto',
            background: '#f6f6f6',
            border: '1px solid #ddd'
          }}
        />
      );
    return row(
      f,
      <input
        id={f}
        type="text"
        name={f}
        placeholder={FIELD_LABELS[f]}
        value={v || ''}
        onChange={handleChange}
        style={{ flex: '1 1 auto' }}
        {...(f === 'pluginCode'
          ? {
              pattern: '[A-Za-z0-9]{4}',
              title: '4 letters/digits, 1+ uppercase'
            }
          : {})}
      />
    );
  };

  if (!roles.includes('developer')) return null;

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <div style={{ color: 'red', fontWeight: 'bold', marginBottom: 16 }}>
        Adding a product will create / overwrite a document in the shared{' '}
        <code>products</code> collection (used by <b>both</b> staging & prod)!
      </div>

      <h2 style={{ marginBottom: 24 }}>Developer Tools</h2>

      {/* —–– Add Product Section —–––––––––––––––––––––––––––––––––––––– */}
      <div
        style={{
          border: '1px solid #ccc',
          borderRadius: 8,
          padding: 16
        }}
      >
        <h3>Add Product to Firestore</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {row(
            'productType',
            <select
              id="productType"
              name="productType"
              value={form.productType}
              onChange={handleChange}
              style={{ flex: '1 1 auto' }}
            >
              <option value="">-- select product type --</option>
              {Object.keys(FIELD_SCHEMA).map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          )}
          {fieldsToRender.map(renderField)}
          <button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Adding…' : 'Add Product'}
          </button>
          <p style={{ minHeight: '1rem', marginTop: '1rem' }}>{status}</p>
        </div>
      </div>
    </div>
  );
}
